import { useEffect, useRef, useState } from 'react'
import { FileUp, Link2, HardDrive, Loader2, Monitor } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Input } from '@orisini/ui/components/input'
import { cn } from '@orisini/ui/lib/utils'
import { ApiError } from '@orisini/ui/lib/api'
import { mediaApi, type MediaDto } from '../api'

type Tab = 'upload' | 'url' | 'local'

const TABS: Array<{ id: Tab; label: string; icon: typeof FileUp }> = [
  { id: 'upload', label: 'Upload', icon: FileUp },
  { id: 'url', label: 'Web URL', icon: Link2 },
  { id: 'local', label: 'Local file', icon: HardDrive },
]

/**
 * Pemilih media tiga sumber — MURNI PENAMBAH.
 *
 * Ketiganya sengaja setara di UI karena ketiganya setara di backend:
 * `r2` (diunggah), `url` (media di web), `local` (path di komputer user).
 *
 * Berbeda dari versi Pinboss yang memegang satu media dan menampilkan
 * pratinjaunya: satu post Instagram bisa berisi sampai 10 slide, jadi
 * pratinjau dan pengurutannya milik daftar slide di komposer, bukan di sini.
 */
export default function MediaPicker({
  onPick,
  videoOnly = false,
}: {
  onPick: (media: MediaDto) => void
  /** Untuk reel: dialog berkas hanya menawarkan video. */
  videoOnly?: boolean
}) {
  const [tab, setTab] = useState<Tab>('upload')
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [urlValue, setUrlValue] = useState('')
  const [pathValue, setPathValue] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)
  // Pemilih berkas lewat agent: dialognya terbuka di komputer user, halaman
  // ini hanya menunggu jawabannya.
  const [menunggu, setMenunggu] = useState<{ hostname: string | null } | null>(null)
  const [pilihPc, setPilihPc] = useState<Array<{ deviceId: string; hostname: string | null }> | null>(
    null,
  )
  /** Nomor putaran polling yang sah; ganti nilainya = hentikan polling lama. */
  const putaran = useRef(0)
  useEffect(() => () => void putaran.current++, [])

  async function run<T>(fn: () => Promise<T>) {
    setBusy(true)
    try {
      return await fn()
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err))
      return null
    } finally {
      setBusy(false)
      setProgress(0)
    }
  }

  async function pakaiLokal(path: string) {
    const r = await run(() => mediaApi.local(path))
    if (r) {
      onPick(r.media)
      setPathValue('')
    }
  }

  async function pilihLewatAgent(deviceId?: string) {
    setPilihPc(null)
    const mulai = await run(() => mediaApi.pickLocal(videoOnly ? ['video'] : ['image', 'video'], deviceId))
    if (!mulai) return
    if ('chooseDevice' in mulai) {
      setPilihPc(mulai.chooseDevice)
      return
    }

    const saya = ++putaran.current
    setMenunggu({ hostname: mulai.hostname })
    try {
      // Batas sisi klien sama dengan TTL di server (10 menit); setelah itu
      // server juga sudah menganggapnya kedaluwarsa.
      const batas = Date.now() + 10 * 60_000
      while (Date.now() < batas) {
        await new Promise((r) => setTimeout(r, 1000))
        if (saya !== putaran.current) return
        const hasil = await mediaApi.pickResult(mulai.requestId)
        if (saya !== putaran.current) return
        if (hasil.status === 'pending') continue
        if (hasil.status === 'picked') {
          setPathValue(hasil.path)
          setMenunggu(null)
          await pakaiLokal(hasil.path)
        } else if (hasil.status === 'error') {
          toast.error(hasil.error)
        } else if (hasil.status !== 'cancelled') {
          toast.error('The file dialog did not answer. Try again.')
        }
        return
      }
      toast.error('The file dialog did not answer. Try again.')
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err))
    } finally {
      if (saya === putaran.current) setMenunggu(null)
    }
  }

  async function onFile(file: File) {
    const media = await run(() => mediaApi.upload(file, setProgress))
    if (media) {
      onPick(media)
      toast.success('Media uploaded')
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex gap-1 rounded-md bg-muted p-1">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-sm transition-colors',
              tab === id
                ? 'bg-background font-medium shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </button>
        ))}
      </div>

      {tab === 'upload' && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            const file = e.dataTransfer.files[0]
            if (file) void onFile(file)
          }}
          className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center"
        >
          <input
            ref={fileRef}
            type="file"
            accept={
              videoOnly
                ? 'video/mp4,video/quicktime,video/x-m4v'
                : 'image/jpeg,image/png,image/webp,image/gif,video/mp4,video/quicktime,video/x-m4v'
            }
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) void onFile(file)
            }}
          />
          {busy ? (
            <>
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Uploading… {progress}%</p>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Drag a file here, or</p>
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
                Choose file
              </Button>
              <p className="text-xs text-muted-foreground">
                {videoOnly ? 'MP4, MOV, M4V' : 'JPG, PNG, WEBP, GIF · MP4, MOV, M4V'}
              </p>
            </>
          )}
        </div>
      )}

      {tab === 'url' && (
        <div className="flex gap-2">
          <Input
            placeholder="https://example.com/image.jpg"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
          />
          <Button
            type="button"
            disabled={busy || !urlValue.trim()}
            onClick={() =>
              void run(() => mediaApi.fromUrl(urlValue.trim())).then((r) => {
                if (r) {
                  onPick(r.media)
                  setUrlValue('')
                }
              })
            }
          >
            {busy ? <Loader2 className="animate-spin" /> : 'Use'}
          </Button>
        </div>
      )}

      {tab === 'local' && (
        <div className="space-y-3">
          {menunggu ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center">
              <Loader2 className="size-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Choose a file in the window that opened
                {menunggu.hostname ? ` on ${menunggu.hostname}` : ' on your computer'}…
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  putaran.current++
                  setMenunggu(null)
                }}
              >
                Stop waiting
              </Button>
            </div>
          ) : pilihPc ? (
            <div className="space-y-2 rounded-lg border p-3">
              <p className="text-sm">Which computer has the file?</p>
              <div className="flex flex-wrap gap-2">
                {pilihPc.map((d) => (
                  <Button
                    key={d.deviceId}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => void pilihLewatAgent(d.deviceId)}
                  >
                    <Monitor className="size-3.5" />
                    {d.hostname ?? d.deviceId}
                  </Button>
                ))}
                <Button type="button" variant="ghost" size="sm" onClick={() => setPilihPc(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center">
              <Button
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => void pilihLewatAgent()}
              >
                {busy ? <Loader2 className="animate-spin" /> : 'Choose file…'}
              </Button>
              <p className="text-xs text-muted-foreground">
                Opens a file window on the computer running the Orisini agent.
              </p>
            </div>
          )}

          <div className="flex gap-2">
            <Input
              placeholder="Or type a path: C:\Users\Name\Pictures\post.jpg"
              value={pathValue}
              onChange={(e) => setPathValue(e.target.value)}
            />
            <Button
              type="button"
              disabled={busy || !pathValue.trim()}
              onClick={() => void pakaiLokal(pathValue.trim())}
            >
              {busy ? <Loader2 className="animate-spin" /> : 'Use'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            The file is not uploaded to the server — the desktop app reads it directly when posting,
            so it must stay at this path until the post is published.
          </p>
        </div>
      )}
    </div>
  )
}
